import { QueryParamsForAssignRiders } from '../../organizers/types';
import { RiderLocation } from '../../riders/features/RiderLocations/Location/Location.types';
import { RideForRiderCentric } from '../../riders/features/RidesList/types';
import {
  deleteRequest,
  getRequest,
  getWithParamsRequest,
  patchRequest,
  patchRequestWithFormData,
  postRequest,
  postRequestWithFormData,
  putRequest,
  putRequestWithFormData,
} from '../utils/provider';
import { ApiResponse } from '../utils/responseTransform';

export interface RidersMeta {
  totalCount: number;
  perPage: number;
  page: number;
}

export interface BulkOperationResult {
  passengerId: number;
  status: 'success' | 'error';
  error?: string;
}

export async function getRiders(
  queryParams: QueryParamsForRiders,
  signal?: AbortSignal
): Promise<ApiResponse<{ riders: RiderForList[]; meta?: RidersMeta }>> {
  return getWithParamsRequest('passengers', queryParams, signal);
}

export async function getRiderFilterOptions(
  signal?: AbortSignal
): Promise<ApiResponse<{ filterOptions: RiderFilterOptions }>> {
  return getRequest('passengers/filter_options', signal);
}

export async function getRidersAvailableAlpha(
  signal?
): Promise<ApiResponse<{ searchableChars: RiderAvailableAlpha[] }>> {
  return getRequest('passengers/available_passenger_search_alphas?with_state=true', signal);
}

export async function getRiderBySlug(passengerSlug: string): Promise<ApiResponse<{ passenger: RiderDetails }>> {
  return getRequest(`passengers/${passengerSlug}`);
}

export async function getRideSchedule(passengerSlug: string): Promise<ApiResponse<RideSchedule>> {
  return getRequest(`passengers/${passengerSlug}/upcoming_schedule`);
}

export async function getCategories(
  signal?: AbortSignal
): Promise<ApiResponse<{ categories: { label: string; value: string }[] }>> {
  return getRequest('passengers/categories', signal);
}

export async function addRider(data: RiderFormType): Promise<ApiResponse<{ slug: string }>> {
  return postRequestWithFormData('passengers', data);
}

export async function updateRiderWithPhoto(id: string, data: RiderFormType): Promise<ApiResponse> {
  return putRequestWithFormData(`passengers/${id}`, data);
}

export async function updateRider(id: string, data: Partial<RiderFormType>): Promise<ApiResponse> {
  return putRequest(`passengers/${id}`, data);
}

export async function updatePartialRider(id: string, data: Partial<RiderFormType>): Promise<ApiResponse> {
  return patchRequest(`passengers/${id}`, data);
}

export async function updatePartialRiderWithPhoto(
  id: string,
  data: RiderFormType
): Promise<ApiResponse<{ message?: string }>> {
  return patchRequestWithFormData(`passengers/${id}`, data);
}

export async function reactivateRider(slug: string): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`passengers/${slug}/reactivate`, {});
}

export async function deactivateRider(
  slug: string,
  payload: { reasonKey?: string } = {}
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`passengers/${slug}/deactivate`, payload);
}

export async function getDuplicateRider(
  params: Pick<Rider, 'firstName' | 'lastName' | 'gender' | 'dateOfBirth'>,
  signal
): Promise<ApiResponse<{ duplicateRiders: Rider[] }>> {
  return getWithParamsRequest(
    'passengers/potential_duplicate_riders',
    { ...params, only: 'slug,first_name,last_name,gender,date_of_birth,profile_photo_url' },
    signal
  );
}

export async function getRides(
  id: string,
  query: {
    fromDate?: string | Date;
    page?: number;
    perPage?: number;
    state?: string;
    startDate?: string | Date;
    endDate?: string | Date;
  },
  signal?
): Promise<ApiResponse<{ trips: RideForRiderCentric[] }>> {
  return getWithParamsRequest(`passengers/${id}/trips`, query, signal);
}

export async function getRequestedRidesExistence(
  id: string,
  query: { [x: string]: unknown },
  signal?
): Promise<ApiResponse<{ criteriaMatched: boolean }>> {
  return getWithParamsRequest(`passengers/${id}/trips/existence`, query, signal);
}

export async function getAssociatedLocations(id: string): Promise<ApiResponse<{ locations: RiderLocation[] }>> {
  return getRequest(`passengers/${id}/locations`);
}

export async function getAssociatedLocationDetails(
  passengerId: string,
  locationId: string | number
): Promise<ApiResponse<{ location: RiderLocation }>> {
  return getRequest(`passengers/${passengerId}/locations/${locationId}`);
}

export async function associateLocation(
  riderSlug: string,
  locationId: number,
  data
): Promise<ApiResponse<{ message: string }>> {
  return putRequest(`passengers/${riderSlug}/locations/${locationId}/associate`, data);
}

export async function dissociateLocation(riderSlug: string, locationId: number): Promise<ApiResponse> {
  return deleteRequest(`passengers/${riderSlug}/locations/${locationId}/dissociate`);
}

export async function getRideRequests(riderSlug: string): Promise<ApiResponse> {
  return getRequest(`passengers/${riderSlug}/ride_requests`);
}

export async function updateRideRequests(
  riderSlug: string,
  rideRequestIds: string,
  rideRequestAction: 'accept' | 'reject' | 'propose'
): Promise<ApiResponse> {
  return putRequest(`passengers/${riderSlug}/ride_requests`, {
    passengerId: riderSlug,
    rideRequestIds,
    rideRequestAction,
  });
}

export type ValidationErrors = {
  email: string;
  gender: string;
  codeWord: string;
  profilePhoto: string;
  contactError: {
    fullName: string;
    phoneNumber: string;
    dateOfBirth: string;
  };
  passengerContact: {
    fullName: string;
    role: string;
    relationship: string;
    email: string;
  };
  sharedBillingRider: {
    residenceAccountContactName: string;
    residenceAccountContactEmail: string;
    residenceAccountId: string;
  };
};

export async function validateRiderDetails(data: Partial<RiderFormType>) {
  return postRequestWithFormData<{ errors: ValidationErrors }, { errors: ValidationErrors }>(
    'passengers/validate',
    data
  );
}

export async function confirmSharedBillingRiderDetails(
  signal: AbortSignal,
  params: {
    slug?: string;
    inactive?: boolean;
    residenceAccountId?: Nullable<number>;
    residenceAccountContactName?: string;
    residenceAccountContactEmail?: string;
  }
): Promise<ApiResponse<{ message: string }>> {
  return getWithParamsRequest('passengers/shared_billing_rider_conflicts', params, signal);
}

export async function getGenders(
  signal?: AbortSignal
): Promise<ApiResponse<{ genders: { label: string; value: string }[] }>> {
  return getRequest('passengers/genders', signal);
}

export async function pauseRider(
  slug: string,
  params: { startDate: Nullable<string>; endDate: Nullable<string> },
  signal?: AbortSignal
): Promise<ApiResponse> {
  return putRequest(`passengers/${slug}/pause`, params, signal);
}

export async function unpauseRider(
  slug: string,
  params: { startDate: string },
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return putRequest(`passengers/${slug}/unpause`, params, signal);
}

export async function updatePauseRider(
  slug: string,
  params: { startDate: Nullable<string>; endDate: Nullable<string> },
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`passengers/${slug}/pauses`, params, signal);
}

export async function editUpdatePauseRider(
  slug: string,
  params: { startDate: Nullable<string>; endDate: Nullable<string> },
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return patchRequest(`passengers/${slug}/pauses`, params, signal);
}

export async function updateUnpauseRider(
  slug: string,
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`passengers/${slug}/pauses/unschedule`, signal);
}

export async function cancelAllRides(slug: string): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`passengers/${slug}/trips/cancel`, {});
}

export async function cancelAllRidesV2(
  slug: string,
  payload: { reasonKey: string }
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`passengers/${slug}/trips/cancel`, payload);
}

export type sharedRiderRequestPayload = {
  shared_billing_rider: {
    uuid: string;
    token: string;
    shareRequestApproved: boolean;
  };
};

export async function sharedRiderRequest(
  payload: sharedRiderRequestPayload,
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return postRequest('passengers/shared_billing/share_request', payload, signal);
}

export async function bulkDeactivateRiders(
  passengerIds: number[] | 'all',
  queryParams?: QueryParamsForAssignRiders,
  reasonKey?: string
): Promise<ApiResponse<{ results: BulkOperationResult[] }>> {
  return postRequest('passengers/bulk_deactivate', { passengerIds, reasonKey, ...queryParams });
}

export async function bulkReactivateRiders(
  passengerIds: number[] | 'all',
  queryParams?: QueryParamsForAssignRiders
): Promise<ApiResponse<{ results: BulkOperationResult[] }>> {
  return postRequest('passengers/bulk_reactivate', { passengerIds, ...queryParams });
}
